import React, { useState, useEffect, useCallback } from 'react';
import { Button, Alert, Spinner, Badge, Card, Row, Col, Form } from 'react-bootstrap';
import { FaCheckCircle, FaSearch, FaTrash, FaEdit } from 'react-icons/fa';
import moment from 'moment';
import AsyncSelect from 'react-select/async';
import { filterData, patchPatronesLote } from '../../../../api/common';
import { Alertassw } from '../../../Alertas/Alertassw';

/**
 * Paso 1 del stepper de RegistroMediciones: SELECCIÓN de patrones.
 *
 * UI:
 *   - AsyncSelect searchable a la DB (colección `Patrones`) por nombre/inventario/serie/marca/código.
 *   - Lista visual de los patrones ya seleccionados con opción de eliminar.
 *   - El select permanece activo para agregar o cambiar patrones.
 *
 * Persistencia: `PATCH /lotes-calibracion/:id/patrones` con array de ObjectId puros
 * (el schema define `patrones: [{type: ObjectId, ref: 'Patrones'}]`).
 * Los snapshots (código/nombre/etc.) los provee el populate al leer el lote.
 *
 * Spec: openspec/changes/lotes-tabs-refresh-plantilla/specs/lotes-paso-patrones-corregido
 */

const selectStyles = {
    menu: (base) => ({ ...base, zIndex: 9999 }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    control: (base) => ({ ...base, minHeight: 44 }),
    option: (base, state) => ({
        ...base,
        fontSize: 13,
        padding: '8px 12px',
        backgroundColor: state.isFocused ? '#e7f1ff' : state.isSelected ? '#cfe2ff' : 'white',
        color: '#212529',
    }),
};

const patronToOption = (p) => ({
    value: p._id,
    label: `${p.codigo || 'S/C'} — ${p.nombre || p.item || 'Sin nombre'}${p.serie ? ` · Serie ${p.serie}` : ''}${p.marca ? ` · ${p.marca}` : ''}`,
    raw: p,
});

const StepSeleccionPatrones = ({ loteEquipos, lotesCalibracion, plantilla, onDataChange }) => {
    // seleccionadosDocs = patrones principales (cuentan hacia cantRequerida).
    // mediosSeleccionadosDocs = medios de calibración opcionales (NO cuentan).
    const [seleccionadosDocs, setSeleccionadosDocs] = useState([]);
    const [mediosSeleccionadosDocs, setMediosSeleccionadosDocs] = useState([]);
    const [guardando, setGuardando] = useState(false);
    const [inicializando, setInicializando] = useState(true);
    // Modo confirmado: bloquea edición hasta que el usuario haga click en "Editar patrones".
    const [confirmado, setConfirmado] = useState(false);

    // Prioridad: override per-lote (Step 1 permite personalizar) → fallback a plantilla.
    const cantRequerida = (lotesCalibracion?.[0]?.cantPatronesRequeridos ?? plantilla?.cantPatronesRequeridos) || 0;

    // Split de patrones de la plantilla:
    //   - `patronesPrincipales`: `magnitudesConfig[i].patronesCalibracion[]` deduped.
    //     Estos SÍ se autocargan y cuentan hacia `cantRequerida`.
    //   - `mediosDeLantilla`: `magnitudesConfig[i].medioCalibracion` deduped.
    //     NO se autocargan — se ofrecen abajo como opcionales.
    const patronesPrincipalesDePlantilla = React.useMemo(() => {
        if (!plantilla?.magnitudesConfig) return [];
        const items = [];
        const vistos = new Set();
        for (const m of plantilla.magnitudesConfig) {
            for (const p of (m.patronesCalibracion || [])) {
                if (!p) continue;
                const id = String(p?._id ?? p);
                if (!vistos.has(id)) { vistos.add(id); items.push(p); }
            }
        }
        return items;
    }, [plantilla]);

    const mediosDePlantilla = React.useMemo(() => {
        if (!plantilla?.magnitudesConfig) return [];
        const items = [];
        const vistos = new Set();
        for (const m of plantilla.magnitudesConfig) {
            const c = m.medioCalibracion;
            if (!c) continue;
            const id = String(c?._id ?? c);
            if (!vistos.has(id)) { vistos.add(id); items.push(c); }
        }
        return items;
    }, [plantilla]);

    // IDs de medios (para clasificar los patrones ya guardados en el lote).
    const idsMediosPlantilla = React.useMemo(
        () => new Set(mediosDePlantilla.map((p) => String(p?._id ?? p))),
        [mediosDePlantilla]
    );

    // Hidratar la selección al montar.
    // Prioridad:
    //   1) `lote.patrones` (guardados) — se splitean en principales vs medios
    //      cross-referenciando contra `idsMediosPlantilla`.
    //   2) Sin guardados → pre-fill de `patronesPrincipalesDePlantilla`
    //      (medios NO se autocargan).
    useEffect(() => {
        const patronesGuardados = lotesCalibracion?.[0]?.patrones || [];
        const hidratar = async (fuente) => {
            if (fuente.length === 0) return [];
            const yaPoblados = fuente.every((p) => p && typeof p === 'object' && p.codigo);
            if (yaPoblados) return fuente;
            const ids = fuente.map((p) => String(p?._id ?? p));
            try {
                const res = await filterData('Patrones', [
                    { $match: { $expr: { $in: [{ $toString: '$_id' }, ids] } } },
                ]);
                let payload = res?.data?.data ?? res?.data ?? [];
                if (!Array.isArray(payload) || payload.length === 0) {
                    const all = await filterData('Patrones', [{ $match: { tipoEquipo: 'Patrón' } }]);
                    const allPayload = all?.data?.data ?? all?.data ?? [];
                    payload = (Array.isArray(allPayload) ? allPayload : []).filter((p) =>
                        ids.includes(String(p._id))
                    );
                }
                return payload;
            } catch (err) {
                console.warn('[StepSeleccionPatrones] no se pudo hidratar:', err);
                return [];
            }
        };

        (async () => {
            if (patronesGuardados.length > 0) {
                const docs = await hidratar(patronesGuardados);
                // Split: los que matchean con medios de plantilla van a mediosSeleccionadosDocs.
                const principales = docs.filter((p) => !idsMediosPlantilla.has(String(p._id)));
                const medios = docs.filter((p) => idsMediosPlantilla.has(String(p._id)));
                setSeleccionadosDocs(principales);
                setMediosSeleccionadosDocs(medios);
                // Si ya coincide con la cantidad requerida, empezar en modo confirmado.
                setConfirmado(principales.length === cantRequerida);
            } else {
                const docs = await hidratar(patronesPrincipalesDePlantilla);
                setSeleccionadosDocs(docs);
                setMediosSeleccionadosDocs([]); // medios opcionales, empiezan vacíos
                setConfirmado(false);
            }
            setInicializando(false);
        })();
    }, [lotesCalibracion, patronesPrincipalesDePlantilla, idsMediosPlantilla, cantRequerida]);

    // AsyncSelect: buscar en la colección `Patrones` por texto (multi-campo).
    const loadOptions = useCallback(
        async (inputValue) => {
            const q = (inputValue || '').trim();
            if (!q || q.length < 2) return [];
            try {
                const regex = { $regex: q, $options: 'i' };
                const res = await filterData('Patrones', [
                    {
                        $match: {
                            tipoEquipo: 'Patrón',
                            $or: [
                                { codigo: regex },
                                { nombre: regex },
                                { item: regex },
                                { descripcion: regex },
                                { serie: regex },
                                { marca: regex },
                                { inventario: regex },
                            ],
                        },
                    },
                    { $limit: 30 },
                ]);
                const payload = res?.data?.data ?? res?.data ?? [];
                // Excluir los ya seleccionados del listado del dropdown.
                const idsSeleccionados = seleccionadosDocs.map((p) => String(p._id));
                return (Array.isArray(payload) ? payload : [])
                    .filter((p) => !idsSeleccionados.includes(String(p._id)))
                    .map(patronToOption);
            } catch (err) {
                console.error('[StepSeleccionPatrones] loadOptions:', err);
                return [];
            }
        },
        [seleccionadosDocs]
    );

    const handleAgregar = (option) => {
        if (!option || !option.raw) return;
        if (seleccionadosDocs.length >= cantRequerida) {
            Alertassw(
                'error',
                `Solo puedes seleccionar ${cantRequerida} ${cantRequerida === 1 ? 'patrón' : 'patrones'}`
            );
            return;
        }
        if (seleccionadosDocs.some((p) => String(p._id) === String(option.raw._id))) return;
        // Si el patrón ya está en medios, moverlo (evita duplicar entre listas).
        setMediosSeleccionadosDocs((prev) =>
            prev.filter((p) => String(p._id) !== String(option.raw._id))
        );
        setSeleccionadosDocs((prev) => [...prev, option.raw]);
    };

    const handleEliminar = (id) => {
        setSeleccionadosDocs((prev) => prev.filter((p) => String(p._id) !== String(id)));
    };

    // Toggle de medio de calibración (opcional). NO cuenta hacia cantRequerida.
    const handleToggleMedio = (medioDoc) => {
        const id = String(medioDoc._id);
        setMediosSeleccionadosDocs((prev) => {
            const yaEsta = prev.some((p) => String(p._id) === id);
            return yaEsta ? prev.filter((p) => String(p._id) !== id) : [...prev, medioDoc];
        });
    };

    const handleConfirmar = async () => {
        if (seleccionadosDocs.length !== cantRequerida) {
            Alertassw(
                'error',
                `Selecciona ${cantRequerida} ${cantRequerida === 1 ? 'patrón' : 'patrones'}`
            );
            return;
        }
        setGuardando(true);
        try {
            // Guardamos principales + medios en el mismo array `lote.patrones` — el
            // split se reconstruye al re-hidratar contra `idsMediosPlantilla`.
            // Así el certificado / hoja de trabajo también incluye los medios usados.
            const idsPrincipales = seleccionadosDocs.map((p) => p._id);
            const idsMedios = mediosSeleccionadosDocs.map((p) => p._id);
            const ids = [...idsPrincipales, ...idsMedios];
            await Promise.all(
                (lotesCalibracion || []).map((lc) => patchPatronesLote(lc._id, { patrones: ids }))
            );
            Alertassw('success', 'Patrones confirmados');
            setConfirmado(true);
            if (onDataChange) onDataChange();
        } catch (err) {
            console.error(err);
            Alertassw('error', 'Error al guardar la selección de patrones');
        } finally {
            setGuardando(false);
        }
    };

    const handleEditar = () => setConfirmado(false);

    if (inicializando) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2 text-muted">Cargando patrones seleccionados…</p>
            </div>
        );
    }

    const puedeSeleccionarMas = seleccionadosDocs.length < cantRequerida;
    // Pre-fill de plantilla cuando el lote está vacío pero la plantilla trae patrones principales.
    const patronesGuardados = lotesCalibracion?.[0]?.patrones || [];
    const preFillDeLantilla = patronesGuardados.length === 0
        && patronesPrincipalesDePlantilla.length > 0
        && seleccionadosDocs.length > 0
        && !confirmado;

    // Medios de plantilla disponibles para toggle (excluye los que ya están
    // como principales para no duplicar).
    const mediosDisponibles = mediosDePlantilla.filter(
        (m) => !seleccionadosDocs.some((p) => String(p._id) === String(m?._id ?? m))
    );

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <h5 className="mb-0">Selección de Patrones</h5>
                <Badge bg={seleccionadosDocs.length === cantRequerida ? 'success' : 'secondary'}>
                    {seleccionadosDocs.length} / {cantRequerida} patrones · {mediosSeleccionadosDocs.length} medio(s)
                </Badge>
            </div>

            {/* Banner grande de confirmación — reemplaza toda la UI de edición */}
            {confirmado && (
                <Card className="border-success shadow-sm mb-3 w-100">
                    <Card.Body className="text-center py-4 w-100">
                        <FaCheckCircle className="text-success mb-2" style={{ fontSize: '3rem' }} />
                        <h4 className="text-success mb-1">Patrones confirmados</h4>
                        <p className="text-muted mb-3 small">
                            {seleccionadosDocs.length} patrón(es) principal(es)
                            {mediosSeleccionadosDocs.length > 0 && (
                                <> + {mediosSeleccionadosDocs.length} medio(s) de calibración</>
                            )} guardado(s) en el lote.
                        </p>
                        <Button variant="outline-secondary" size="sm" onClick={handleEditar}>
                            <FaEdit className="me-1" /> Editar patrones
                        </Button>
                    </Card.Body>
                </Card>
            )}

            {!confirmado && preFillDeLantilla && (
                <Alert variant="warning" className="py-2 mb-3">
                    <strong>Patrones sugeridos desde la plantilla.</strong> Si son los
                    mismos que vas a usar, presiona <em>Confirmar patrones</em>. Si no,
                    quítalos y elegí otros.
                </Alert>
            )}

            {!confirmado && (
              <Alert variant="info" className="py-2 mb-3">
                Busca los patrones por <strong>código, nombre, serie, marca o inventario</strong> y
                agrégalos a la lista. Debes seleccionar exactamente <strong>{cantRequerida}</strong>{' '}
                {cantRequerida === 1 ? 'patrón' : 'patrones'} para continuar.
              </Alert>
            )}

            {!confirmado && (
            <Card className="mb-3 shadow-sm w-100">
                <Card.Body className="py-3 w-100">
                    <label className="small fw-semibold mb-1 d-block">
                        <FaSearch className="me-1" /> Buscar patrón para agregar
                    </label>
                    <AsyncSelect
                        cacheOptions={false}
                        loadOptions={loadOptions}
                        onChange={handleAgregar}
                        value={null}
                        placeholder={
                            puedeSeleccionarMas
                                ? 'Escribe al menos 2 caracteres (código, nombre, serie, marca, inventario)…'
                                : `Límite alcanzado (${cantRequerida}). Elimina uno para cambiar.`
                        }
                        isDisabled={!puedeSeleccionarMas || guardando}
                        noOptionsMessage={({ inputValue }) =>
                            !inputValue || inputValue.length < 2
                                ? 'Escribe al menos 2 caracteres para buscar'
                                : 'Sin resultados — intenta con otro texto'
                        }
                        loadingMessage={() => 'Buscando patrones…'}
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                    />
                </Card.Body>
            </Card>
            )}

            {!confirmado && (seleccionadosDocs.length === 0 ? (
                <Alert variant="warning" className="text-center">
                    Aún no has agregado ningún patrón. Usa el buscador de arriba.
                </Alert>
            ) : (
                <div className="d-flex flex-column gap-2 mb-3">
                    {seleccionadosDocs.map((p) => (
                        <Card key={p._id} className="shadow-sm w-100">
                            <Card.Body className="py-2 w-100">
                                <Row className="align-items-center g-2">
                                    <Col>
                                        <div className="fw-semibold">
                                            {p.codigo || 'S/C'} — {p.nombre || p.item || 'Sin nombre'}
                                        </div>
                                        <div className="small text-muted">
                                            {p.marca && <span className="me-2">Marca: {p.marca}</span>}
                                            {p.modelo && <span className="me-2">Modelo: {p.modelo}</span>}
                                            {p.serie && <span className="me-2">Serie: {p.serie}</span>}
                                            {p.inventario && (
                                                <span className="me-2">Inv: {p.inventario}</span>
                                            )}
                                            {p.fechaCalibracion && (
                                                <span className="me-2">
                                                    Últ. cal.: {moment(p.fechaCalibracion).format('DD/MM/YYYY')}
                                                </span>
                                            )}
                                        </div>
                                    </Col>
                                    <Col xs="auto">
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => handleEliminar(p._id)}
                                            disabled={guardando}
                                            title="Quitar de la selección"
                                        >
                                            <FaTrash />
                                        </Button>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    ))}
                </div>
            ))}

            {/* Medios de calibración — opcionales, NO cuentan hacia cantRequerida.
                Solo se listan los definidos en la plantilla; el usuario los activa
                manualmente si aplica. Bloqueados en modo confirmado. */}
            {!confirmado && mediosDePlantilla.length > 0 && (
                <Card className="mb-3 shadow-sm border-info w-100">
                    <Card.Header className="bg-light w-100">
                        <span className="fw-semibold">Medios de calibración</span>
                        <small className="text-muted ms-2">
                            (opcional — no cuentan como patrones requeridos)
                        </small>
                    </Card.Header>
                    <Card.Body className="py-2 w-100">
                        <div className="d-flex flex-column gap-2">
                            {mediosDePlantilla.map((m) => {
                                const id = String(m?._id ?? m);
                                const activo = mediosSeleccionadosDocs.some((x) => String(x._id) === id);
                                const yaEsPrincipal = seleccionadosDocs.some((x) => String(x._id) === id);
                                return (
                                    <Row key={id} className="align-items-center g-2">
                                        <Col xs="auto">
                                            <Form.Check
                                                type="checkbox"
                                                id={`medio-${id}`}
                                                checked={activo}
                                                onChange={() => handleToggleMedio(m)}
                                                disabled={guardando || yaEsPrincipal}
                                                title={yaEsPrincipal ? 'Ya está incluido como patrón principal' : ''}
                                            />
                                        </Col>
                                        <Col>
                                            <div className="fw-semibold small">
                                                {m.codigo || 'S/C'} — {m.nombre || m.item || 'Sin nombre'}
                                            </div>
                                            <div className="small text-muted">
                                                {m.marca && <span className="me-2">Marca: {m.marca}</span>}
                                                {m.serie && <span className="me-2">Serie: {m.serie}</span>}
                                                {m.inventario && <span className="me-2">Inv: {m.inventario}</span>}
                                            </div>
                                        </Col>
                                    </Row>
                                );
                            })}
                        </div>
                    </Card.Body>
                </Card>
            )}

            {!confirmado && (
                <div className="text-center mt-3">
                    <Button
                        variant="success"
                        onClick={handleConfirmar}
                        disabled={guardando || seleccionadosDocs.length !== cantRequerida}
                        size="lg"
                    >
                        {guardando ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Guardando…
                            </>
                        ) : (
                            <>
                                <FaCheckCircle className="me-2" />
                                Confirmar patrones
                            </>
                        )}
                    </Button>
                </div>
            )}
        </div>
    );
};

export default StepSeleccionPatrones;
